<script setup>
import {
  ExportOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message, Modal } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import {
  deleteRequest,
  exportFile,
  getRequest,
  postRequest,
} from "@/utils/request.js";
import CompanyInfoModal from "@/views/company/modules/CompanyInfoModal.vue";
import CompanyAuditFormModal from "@/views/company/audit/modules/CompanyAuditFormModal.vue";
import JobInfoModal from "@/views/job/modules/JobInfoModal.vue";
import JobFormModal from "@/views/job/modules/JobFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const formModalRef = ref();
const cityOptions = ref([]);
const selectedDistrict = ref([]);

const columns = [
  {
    key: "title",
    dataIndex: "title",
    title: "职位标题",
    width: "10%",
  },
  {
    key: "name",
    dataIndex: "name",
    title: "职位名称",
    width: "5%",
  },
  {
    key: "jobTypeName",
    dataIndex: "jobTypeName",
    title: "工种名称",
    width: "5%",
  },
  {
    key: "workingType",
    dataIndex: "workingType",
    title: "职位类型",
    width: "5%",
  },
  {
    key: "wage",
    dataIndex: "wage",
    title: "薪资",
    width: "8%",
  },
  {
    key: "headcount",
    dataIndex: "headcount",
    title: "招聘人数",
    width: "5%",
  },
  {
    key: "phone",
    dataIndex: "phone",
    title: "联系电话",
    width: "10%",
  },
  {
    key: "recruiterRealName",
    dataIndex: "recruiterRealName",
    title: "发布人",
    width: "5%",
  },
  {
    key: "companyName",
    dataIndex: "companyName",
    title: "所属企业",
    width: "15%",
  },
  {
    key: "address",
    dataIndex: "address",
    title: "地址",
    width: "15%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "启用状态",
    width: "5%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadCity = () => {
  getRequest("/city").then((res) => {
    if (res.success) {
      const cityData = res.data.map((province) => {
        const children = province.cityList.map((city) => {
          const { id, name } = city;
          return { id, name, children: city.districtList };
        });

        const { id, name } = province;
        return { id, name, children };
      });
      console.info("cityData", cityData);
      cityOptions.value = cityData;
    }
  });
};

const loadData = () => {
  loading.value = true;

  const [provinceId, cityId, districtId] = unref(selectedDistrict);

  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign(
    {},
    unref(searchFormModel),
    {
      pageNo: current,
      pageSize,
    },
    { provinceId, cityId, districtIds: [districtId] },
  );

  getRequest("/job", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const resetForm = () => {
  formRef.value.resetFields();
  selectedDistrict.value = [];
  loadData();
};

const handlePreview = (record) => {
  modalRef.value.title = record.title;
  modalRef.value.handleShow(record);
};
const handleEditView = (record) => {
  formModalRef.value.title = "职位编辑";
  formModalRef.value.handleEdit(record);
};

const handleOpen = (record) => {
  Modal.confirm({
    title: `确定启用？`,
    content: `确定要将【${record.companyName}的${record.name}】启用吗？`,
    onOk() {
      postRequest(`/job/open/${record.id}`).then((response) => {
        if (response.success) {
          loadData();
        } else {
          message.error(response.message);
        }
      });
    },
    onCancel() {},
  });
};

const handleClose = (record) => {
  Modal.confirm({
    title: `确定停用？`,
    content: `确定要将【${record.companyName}的${record.name}】停用吗？`,
    onOk() {
      postRequest(`/job/close/${record.id}`).then((response) => {
        if (response.success) {
          loadData();
        } else {
          message.error(response.message);
        }
      });
    },
  });
};

const handleRemove = (record) => {
  Modal.confirm({
    title: `确定删除？`,
    content: `确定要将【${record.companyName}的${record.name}】删除吗？`,
    onOk() {
      deleteRequest(`/job/${record.id}`).then((response) => {
        if (response.success) {
          loadData();
        } else {
          message.error(response.message);
        }
      });
    },
  });
};

const exporting = ref(false);
const exportExcel = () => {
  const requestBody = Object.assign({}, unref(searchFormModel));
  exporting.value = true;
  exportFile("/job/export", requestBody, "职位导出.xlsx").finally(() => {
    exporting.value = false;
  });
};

onMounted(() => {
  loadCity();
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="职位标题" name="title">
              <a-input
                v-model:value.trim="searchFormModel.title"
                placeholder="请输入职位标题"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="职位名称" name="name">
              <a-input
                v-model:value.trim="searchFormModel.name"
                placeholder="请输入职位名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="职位类型" name="workingType">
              <a-select
                v-model:value="searchFormModel.workingType"
                placeholder="请输入职位类型"
              >
                <a-select-option value="FULL"> 全职</a-select-option>
                <a-select-option value="PART"> 兼职</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="发布人" name="recruiterRealName">
              <a-input
                v-model:value.trim="searchFormModel.recruiterRealName"
                placeholder="请输入发布人姓名"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="所属企业" name="companyName">
              <a-input
                v-model:value.trim="searchFormModel.companyName"
                placeholder="请输入所属企业"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="地区" name="district">
              <a-cascader
                v-model:value="selectedDistrict"
                :options="cityOptions"
                :fieldNames="{
                  label: 'name',
                  value: 'id',
                }"
                change-on-select
                placeholder="请选择地区"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button
          type="primary"
          @click="exportExcel"
          :icon="h(ExportOutlined)"
          :loading="exporting"
        >
          导出
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'wage'">
            <template
              v-if="!record['wageUnit'] || record['wageUnit'] === '面议'"
            >
              薪资面议
            </template>
            <template v-else>
              {{ record["minWage"] }}-{{ record["maxWage"]
              }}{{ record["wageUnit"] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'address'">
            <div>
              {{ record.provinceName }}{{ record.cityName
              }}{{ record.districtName }}
            </div>
            <div>{{ record.address }}</div>
          </template>

          <template v-if="column.dataIndex === 'workingType'">
            <template v-if="record[column.dataIndex] === 'FULL'">
              全职
            </template>
            <template v-else-if="record[column.dataIndex] === 'PART'">
              兼职
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex] === 'OPEN'">
              <a-tag color="green">招聘中</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'CLOSE'">
              <a-tag color="red">停止招聘</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">{{ record[column.dataIndex] }}</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handlePreview(record)">查看</a>
			 <a-divider type="vertical" />
			 <a type="link" @click="handleEditView(record)">编辑</a>
            <a-divider type="vertical" />
            <template v-if="record['status']">
              <a type="link" @click="handleClose(record)">停用</a>
            </template>
            <template v-else>
              <a type="link" @click="handleOpen(record)">启用</a>
            </template>
            <a-divider type="vertical" />
            <a type="link" @click="handleRemove(record)">删除</a>
          </template>
        </template>
      </a-table>
    </a-card>
    <job-info-modal ref="modalRef" />
	<job-form-modal ref="formModalRef"  @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
